import React from 'react';
import moment from 'moment';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';

// Ortak renk token'ları
export const c = {
    text: '#1f2937',
    sub: '#6b7280',
    line: '#e6e9ef',
    lineSoft: '#eef0f4',
    soft: '#f8fafc',
    red: '#dc2626',
    blue: '#2563eb',
};

// status -> Türkçe etiket + renk (contract: 0 Error, 1 Success, 2 Start, 3 Pending, 4 Cancel/Refund, 5 Payment Link)
export const STATUS_STYLES = {
    0: {label: 'Hata', color: '#dc2626', bg: '#fef2f2'},
    1: {label: 'Başarılı', color: '#059669', bg: '#ecfdf5'},
    2: {label: 'Başladı', color: '#2563eb', bg: '#eff6ff'},
    3: {label: 'Beklemede', color: '#d97706', bg: '#fffbeb'},
    4: {label: 'İptal/İade', color: '#7c3aed', bg: '#f5f3ff'},
    5: {label: 'Ödeme Linki', color: '#475569', bg: '#f1f5f9'},
};
export const statusMeta = (status) => STATUS_STYLES[status] || {label: status, color: '#475569', bg: '#f1f5f9'};
export const statusLabel = (status) => statusMeta(status).label;

export const ACTION_META = {
    refund: {label: 'İade', color: '#2563eb', bg: '#eff6ff'},
    cancel: {label: 'İptal', color: '#7c3aed', bg: '#f5f3ff'},
    update: {label: 'Güncelleme', color: '#475569', bg: '#f1f5f9'},
};
export const actionMeta = (action) => ACTION_META[action] || {label: action || 'Güncelleme', color: '#475569', bg: '#f1f5f9'};
export const actionLabel = (action) => actionMeta(action).label;

export const fmtDate = (unix) => unix ? moment.unix(unix).format('DD-MM-YYYY HH:mm:ss') : '-';

export const userText = (u) => u
    ? `${u.nameSurname || `${u.name || ''} ${u.surname || ''}`.trim()}${u.email ? ` (${u.email})` : ''}`
    : '-';

const styles = {
    auditCard: {border: `1px solid ${c.line}`, borderRadius: 12, padding: 16, marginBottom: 12, background: '#fff'},
    auditHead: {display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap'},
    metaRow: {display: 'flex', gap: 6, fontSize: 13, color: c.sub, padding: '2px 0'},
    timeline: {marginTop: 12, paddingLeft: 4},
    tlItem: {display: 'flex', gap: 12, position: 'relative', paddingBottom: 14},
    tlDot: (color) => ({
        width: 10, height: 10, borderRadius: '50%', background: color,
        marginTop: 5, flexShrink: 0, boxShadow: `0 0 0 3px ${color}22`,
    }),
    actionBadge: (color, bg) => ({
        display: 'inline-block', fontSize: 11, fontWeight: 700, color, background: bg,
        borderRadius: 6, padding: '1px 8px', marginRight: 8, textTransform: 'uppercase', letterSpacing: '.02em',
    }),
};

export const StatusPill = ({status, small}) => {
    const m = statusMeta(status);
    return (
        <span style={{
            display: 'inline-block', fontSize: small ? 11 : 12, fontWeight: 700, color: m.color, background: m.bg,
            borderRadius: 999, padding: small ? '2px 8px' : '3px 10px', whiteSpace: 'nowrap',
        }}>
            {m.label}
        </span>
    );
};

// Bir history satırındaki gerçek değişimleri (durum / tutar) özetler.
export const HistoryChange = ({h, currency}) => {
    const statusChanged = h.old_status !== h.new_status;
    const amountChanged = h.old_amount !== undefined && h.old_amount !== null
        && Number(h.old_amount) !== Number(h.new_amount);
    const hasRefund = h.refund_amount !== undefined && h.refund_amount !== null && Number(h.refund_amount) > 0;

    return (
        <span style={{display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 8}}>
            {statusChanged && (
                <span style={{display: 'inline-flex', alignItems: 'center', gap: 6}}>
                    <StatusPill status={h.old_status} small/>
                    <i className="fas fa-arrow-right" style={{fontSize: 10, color: c.sub}}/>
                    <StatusPill status={h.new_status} small/>
                </span>
            )}
            {amountChanged && (
                <span style={{fontSize: 13, color: c.text, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6}}>
                    {h.old_amount} {currency}
                    <i className="fas fa-arrow-right" style={{fontSize: 10, color: c.sub}}/>
                    {h.new_amount} {currency}
                </span>
            )}
            {hasRefund && (
                <span style={{
                    fontSize: 12, fontWeight: 700, color: c.blue, background: '#eff6ff',
                    borderRadius: 6, padding: '1px 8px',
                }}>
                    −{h.refund_amount} iade
                </span>
            )}
            {!statusChanged && !amountChanged && !hasRefund && (
                <span style={{fontSize: 13, color: c.sub}}>Güncelleme</span>
            )}
        </span>
    );
};

const AuditTransactionCard = ({tr}) => (
    <div style={styles.auditCard}>
        <div style={styles.auditHead}>
            <span style={{fontWeight: 700, color: c.text, fontSize: 14}}>{tr.code || tr.order_id}</span>
            <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                <span style={{fontWeight: 700, color: c.text}}>{tr.amount} {tr.currency}</span>
                <StatusPill status={tr.status}/>
            </div>
        </div>

        {tr.created_user && <div style={styles.metaRow}><span style={{color: '#9ca3af'}}>Oluşturan:</span><span style={{color: c.text}}>{userText(tr.created_user)}</span></div>}
        {tr.updated_user && <div style={styles.metaRow}><span style={{color: '#9ca3af'}}>Son Güncelleyen:</span><span style={{color: c.text}}>{userText(tr.updated_user)}</span></div>}

        {Array.isArray(tr.history) && tr.history.length > 0 ? (
            <div style={styles.timeline}>
                {tr.history.map((h, hi) => (
                    typeof h === 'string' ? (
                        <div key={hi} style={styles.tlItem}>
                            <span style={styles.tlDot(c.blue)}/>
                            <div style={{color: '#374151', fontSize: 13}}>{h}</div>
                        </div>
                    ) : (
                        <div key={h.id} style={styles.tlItem}>
                            <span style={styles.tlDot(actionMeta(h.action).color)}/>
                            <div>
                                <div style={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 3}}>
                                    <span style={styles.actionBadge(actionMeta(h.action).color, actionMeta(h.action).bg)}>{actionLabel(h.action)}</span>
                                    <HistoryChange h={h} currency={h.currency || tr.currency || ''}/>
                                </div>
                                <div style={{color: '#9ca3af', fontSize: 12.5}}>
                                    {h.user && h.user.nameSurname ? h.user.nameSurname : `user:${h.created_by || '-'}`} · {fmtDate(h.created_at)}
                                    {h.message ? ` · ${h.message}` : ''}
                                </div>
                            </div>
                        </div>
                    )
                ))}
            </div>
        ) : (
            <Typography variant="body2" style={{color: '#9ca3af', marginTop: 6}}>Geçmiş kaydı yok.</Typography>
        )}
    </div>
);

/**
 * Audit işlemlerini (kim ne yaptı) listeler.
 * props: transactions[], loading, emptyText, filterCode (verilirse sadece o code'un raporu)
 */
export const AuditTransactionList = ({transactions, loading, emptyText = 'Kayıt bulunamadı.', filterCode}) => {
    const list = Array.isArray(transactions) ? transactions : [];
    const shown = filterCode ? list.filter(t => t.code === filterCode) : list;

    if (loading) {
        return (
            <div style={{display: 'flex', alignItems: 'center', gap: 8, color: c.sub, padding: '8px 0'}}>
                <CircularProgress className="audit-circular" size={16}/>
                <style>{`.audit-circular {position: relative !important;}`}</style>
                <Typography variant="body2">Audit yükleniyor...</Typography>
            </div>
        );
    }

    if (shown.length === 0) {
        return <Typography variant="body2" style={{color: '#9ca3af', padding: '8px 0'}}>{emptyText}</Typography>;
    }

    return (
        <>
            {shown.map((tr) => <AuditTransactionCard key={tr.id} tr={tr}/>)}
        </>
    );
};
